import React, { useMemo } from "react";
import {
	Annotation,
	TextAnnotation,
} from "./presentationAnnotationTypes";

interface PresentationAnnotationsOverlayProps {
	annotations: Annotation[];
	displayedWidth: number;
	displayedHeight: number;
}

/**
 * Read-only overlay: normalized coords (0..1) are mapped to the displayed
 * image wrapper size so annotations align with livekit-client on every device.
 */
const PresentationAnnotationsOverlay: React.FC<
	PresentationAnnotationsOverlayProps
> = ({ annotations, displayedWidth, displayedHeight }) => {
	const shapes = useMemo(
		() => annotations.filter((a) => a.kind !== "text"),
		[annotations]
	);
	const textAnnotations = useMemo(
		() => annotations.filter((a): a is TextAnnotation => a.kind === "text"),
		[annotations]
	);

	if (displayedWidth <= 0 || displayedHeight <= 0) {
		return null;
	}

	const dW = displayedWidth;
	const dH = displayedHeight;

	return (
		<div className="presentationAnnotationLayer" aria-hidden>
			<svg
				className="presentationAnnotationSvg"
				viewBox={`0 0 ${dW} ${dH}`}
				preserveAspectRatio="none"
			>
				{shapes.map((a) => {
					const strokePx = Math.max(1, a.strokeWidth * dH);
					if (a.kind === "rect") {
						return (
							<rect
								key={a.id}
								x={a.x * dW}
								y={a.y * dH}
								width={a.w * dW}
								height={a.h * dH}
								fill="transparent"
								stroke={a.color}
								strokeWidth={strokePx}
								rx={Math.min(a.w * dW, a.h * dH) * 0.04}
							/>
						);
					}
					if (a.kind === "arrow") {
						const headId = `arrowhead-${a.id}`;
						return (
							<g key={a.id}>
								<defs>
									<marker
										id={headId}
										markerWidth={5}
										markerHeight={5}
										refX={4.2}
										refY={2.5}
										orient="auto"
										markerUnits="strokeWidth"
									>
										<path d="M0,0 L0,5 L5,2.5 z" fill={a.color} />
									</marker>
								</defs>
								<line
									x1={a.x1 * dW}
									y1={a.y1 * dH}
									x2={a.x2 * dW}
									y2={a.y2 * dH}
									stroke={a.color}
									strokeWidth={strokePx}
									strokeLinecap="round"
									markerEnd={`url(#${headId})`}
								/>
							</g>
						);
					}
					if (a.kind === "pen" && a.points.length >= 2) {
						let pointsStr = "";
						for (let i = 0; i < a.points.length; i += 2) {
							pointsStr += `${a.points[i] * dW},${a.points[i + 1] * dH} `;
						}
						return (
							<polyline
								key={a.id}
								points={pointsStr.trim()}
								fill="none"
								stroke={a.color}
								strokeWidth={strokePx}
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						);
					}
					return null;
				})}
			</svg>
			{textAnnotations.map((a) => {
				const fontSizePx = Math.max(10, a.size * dH);
				return (
					<div
						key={a.id}
						className="presentationAnnotationText"
						style={{
							left: `${a.x * 100}%`,
							top: `${a.y * 100}%`,
							color: a.color,
							fontSize: `${fontSizePx}px`,
							textShadow:
								"0 0 4px rgba(0,0,0,0.7), 0 1px 2px rgba(0,0,0,0.5)",
						}}
					>
						{a.text}
					</div>
				);
			})}
		</div>
	);
};

export default PresentationAnnotationsOverlay;
